import { z } from 'zod';
import { InventoryAdjustReason } from '../../constants/inventory-adjust-reason.js';
import { ADJUST_FIELDS } from '../inventory-adjustment/adjust-fields.js';
import { ADJUST_NOTE_REQUIRED_MESSAGE } from '../inventory-adjustment/adjust-note-required-message.js';
import { isAdjustNoteRequired } from '../inventory-adjustment/is-adjust-note-required.js';
import { isAdjustReasonAllowed } from '../inventory-adjustment/is-adjust-reason-allowed.js';
import { hasAtMostDecimalPlaces } from '../promotion-credit/has-at-most-decimal-places.js';
import { SALES_QTY_DECIMALS } from '../sales-order/sales-qty-decimals.js';
import { optionalUuidString, requiredUuidString } from '../zod-helpers/index.js';
import { MAX_RECEIVE_LINES, MAX_RECEIVE_LINES_MESSAGE } from './constraints.js';
import { RETURN_CLOSE_REASON_MAX_LENGTH } from './return-close-reason-max-length.js';
import { RETURN_DUPLICATE_ROW_MESSAGE } from './return-duplicate-row-message.js';
import { RETURN_OUTCOME_REQUIRED_MESSAGE } from './return-outcome-required-message.js';
import { RETURN_OUTCOMES } from './return-outcome.js';
const returnItemIdSchema = () => requiredUuidString('Return line is required.', 'Invalid return line selection.');
const optionalLocationIdSchema = optionalUuidString('Invalid location selection.')
    .nullish()
    .transform((value) => value || undefined);
const returnLineSchema = z
    .object({
    returnItemId: returnItemIdSchema(),
    // The return columns are numeric(28,9), so a finer quantity would round to a receipt of nothing.
    quantity: ADJUST_FIELDS.quantity().refine((quantity) => hasAtMostDecimalPlaces(quantity, SALES_QTY_DECIMALS), { error: `Enter a quantity with at most ${SALES_QTY_DECIMALS} decimal places.` }),
    disposition: z.enum(RETURN_OUTCOMES, RETURN_OUTCOME_REQUIRED_MESSAGE),
    locationId: optionalLocationIdSchema,
    scrapReasonId: ADJUST_FIELDS.reasonId().nullish(),
    note: ADJUST_FIELDS.note(),
})
    .superRefine((line, context) => {
    if (line.disposition === 'DESTROY') {
        const reasonId = line.scrapReasonId ?? InventoryAdjustReason.DAMAGED;
        if (!isAdjustReasonAllowed('SCRAP', reasonId)) {
            context.addIssue({
                code: 'custom',
                path: ['scrapReasonId'],
                message: 'That reason does not fit a scrap.',
            });
        }
        else if (isAdjustNoteRequired(reasonId) && !line.note) {
            context.addIssue({ code: 'custom', path: ['note'], message: ADJUST_NOTE_REQUIRED_MESSAGE });
        }
        return;
    }
    if (!line.locationId) {
        context.addIssue({ code: 'custom', path: ['locationId'], message: 'Location is required.' });
    }
    if (line.scrapReasonId != null) {
        context.addIssue({
            code: 'custom',
            path: ['scrapReasonId'],
            message: 'A scrap reason applies only to Destroy.',
        });
    }
    if (line.note) {
        context.addIssue({
            code: 'custom',
            path: ['note'],
            message: 'A note applies only to Destroy.',
        });
    }
})
    .transform((line) => ({
    ...line,
    scrapReasonId: line.disposition === 'DESTROY'
        ? (line.scrapReasonId ?? InventoryAdjustReason.DAMAGED)
        : undefined,
}));
const closeLineSchema = z.object({
    returnItemId: returnItemIdSchema(),
    reason: z
        .string({ error: 'Reason is required.' })
        .trim()
        .min(1, 'Reason is required.')
        .max(RETURN_CLOSE_REASON_MAX_LENGTH, `Reason must be ${RETURN_CLOSE_REASON_MAX_LENGTH} characters or fewer.`),
});
function rowKey(line) {
    return `${line.returnItemId}:${line.disposition}:${line.locationId ?? ''}`;
}
export const receiveSalesOrderReturnInputSchema = z
    .object({
    salesOrderId: requiredUuidString('Sales order is required.', 'Invalid sales order selection.'),
    receiptId: requiredUuidString('Receipt id is required.', 'Invalid receipt id.'),
    lines: z
        .array(returnLineSchema)
        .max(MAX_RECEIVE_LINES, MAX_RECEIVE_LINES_MESSAGE)
        .nullish()
        .transform((lines) => lines ?? []),
    closeLines: z
        .array(closeLineSchema)
        .max(MAX_RECEIVE_LINES, MAX_RECEIVE_LINES_MESSAGE)
        .nullish()
        .transform((closeLines) => closeLines ?? []),
})
    .superRefine((input, context) => {
    if (input.lines.length === 0 && input.closeLines.length === 0) {
        context.addIssue({
            code: 'custom',
            path: ['lines'],
            message: 'Receive or close at least one line.',
        });
    }
    const rows = new Set();
    for (const [index, line] of input.lines.entries()) {
        const key = rowKey(line);
        if (rows.has(key)) {
            context.addIssue({
                code: 'custom',
                path: ['lines', index, 'locationId'],
                message: RETURN_DUPLICATE_ROW_MESSAGE,
            });
        }
        rows.add(key);
    }
    const closed = new Set();
    for (const [index, closeLine] of input.closeLines.entries()) {
        if (closed.has(closeLine.returnItemId)) {
            context.addIssue({
                code: 'custom',
                path: ['closeLines', index, 'returnItemId'],
                message: 'This line is already being closed.',
            });
        }
        closed.add(closeLine.returnItemId);
    }
});
//# sourceMappingURL=receive-sales-order-return-input.js.map